import { useCallback, useContext } from "react";

import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { DirectoryHealthCareProvider } from "../Models/DirectoryHealthCareProvider";
import { DirectoryOtherEstablishment } from "../Models/DirectoryOtherEstablishment";
import { DirectoryPharmacy } from "../Models/DirectoryPharmacy";
import { IDirectoryEntity } from "../Models/IDirectoryEntity";
import { PatientPreferredDirectoryContactsContext } from "../Models/PatientPreferredDirectoryContactsContext";

/**
 * Returns a callback that allows updating the patient's store preferred contacts
 * @returns callback
 */
export const useOnPatientStoreUpdateDispatcher = (): { (entity: IDirectoryEntity, type: DirectoryEntityType) } => {
    const {
        patientContactId,
        preferredPharmacy,
        externalConsultants
    } = useContext(PatientPreferredDirectoryContactsContext);

    return useCallback(
        (entity: IDirectoryEntity): void => {
            if (!patientContactId)
                return;

            if (entity.type === DirectoryEntityType.Pharmacy && entity.id === preferredPharmacy?.DirectoryPharmacyId) {
                const pharmacy = entity as DirectoryPharmacy;

                sessionStore.dispatch(
                    LocalPatientOperations.Fax.UpdatePreferredPharmacy,
                    {
                        patientContactId: patientContactId,
                        pharmacy: pharmacy.getPharmacyInfo(),
                        directoryPharmacy: pharmacy
                    }
                );
                return;
            }

            if (entity.type === DirectoryEntityType.HealthcareProvider && externalConsultants.any(p => p.id === entity.id)) {
                const healthCareProvider = entity as DirectoryHealthCareProvider;

                sessionStore.dispatch(
                    LocalPatientOperations.Profile.Clinicians_UpdateHealthCareProvider,
                    { healthCareProvider }
                );
                return;
            }

            if (entity.type === DirectoryEntityType.Other) {
                const establishment = entity as DirectoryOtherEstablishment;

                for (const patientId of Object.keys(patientStore.PatientProfileCliniciansModels)) {
                    const consultants = patientStore.PatientProfileCliniciansModels[patientId]
                        .get("IntervenorsOnFile")
                        ?.filter(p => !!p?.Model?.HealthCareProvider)
                        .toArray();

                    if (!consultants?.any())
                        continue;

                    const linkedEstablishments = consultants?.flatMap(p => p.Model.HealthCareProvider.linkedEstablishments) ?? [];
                    const shouldUpdateExternalConsultants = linkedEstablishments.some(linkedEstablishment => {
                        if (establishment.id !== linkedEstablishment.id)
                            return false;

                        const wasLinkObsolete = linkedEstablishment.faxNumbers[0].isObsoleteFaxNumberLink; // there is always only one fax number per linked establishment
                        const isLinkObsolete = !establishment.faxNumbers
                            .map(p => `${p.countryId}${p.number}`)
                            ?.contains(`${linkedEstablishment.faxNumbers[0].countryId}${linkedEstablishment.faxNumbers[0].number}`);

                        return wasLinkObsolete !== isLinkObsolete;
                    });

                    if (shouldUpdateExternalConsultants)
                        sessionStore.dispatch(LocalPatientOperations.Profile.Clinicians_LoadHealthCareProviders, { patientContactId: patientId });
                }
            }
        },
        [patientContactId, preferredPharmacy, externalConsultants]
    );
};
